import React from 'react';

import {
  addDays,
  areIntervalsOverlapping,
  format,
  isAfter,
  isBefore,
  isValid as isDateValid
} from 'date-fns';

// Material-UI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import HelpIcon from '@mui/icons-material/Help';
import IconButton from '@mui/material/IconButton';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';

import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import CabinIcon from '@mui/icons-material/Cabin';
import DownhillSkiingIcon from '@mui/icons-material/DownhillSkiing';
import HikingIcon from '@mui/icons-material/Hiking';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import SaveIcon from '@mui/icons-material/Save';

import WaiverDialog from '../WaiverDialog';

// Styles
import { useTheme } from '@mui/material/styles';

import {
  HTTPHeaders,
  MemberWaiverStatus,
  PriorityPeriod,
  SignupResultStatus,
  TransferablePassSignup
} from '../../interfaces';

import {
  HouseholdRequestStatus,
  Member,
  MemberSignupInputForm,
  SignupNew,
} from '../../models';

import {
  API_URL,
  EMPTY_STRING,
  RequestDatesType,
  SignupExceptionEnum,
  WaiverStatus
} from '../../global';

import MaxRequestsWarningDialog from './MaxRequestsWarningDialog';
import PostDeadlineWarningDialog from './PostDeadlineWarningDialog';
import PriorityPeriodList from '../../shared/PriorityPeriodList';
import RequestFormHelpDialog from './RequestFormHelpDialog';
import TransferablePasses from '../TransferablePasses';
import WeekendHoliday from './WeekendHoliday';

// Auth0 user hooks 
import { useAuth0User } from '../../hooks/UseAuth0User';

// Date formatting
import { useDates } from '../../hooks/UseDates';

// Signup date criteria
import { useSignupDates } from '../../hooks/UseSignupDates';

// Responsive hooks
import { useResponsive } from '../../hooks/UseResponsive';

// Redux
import { useAppSelector } from '../../hooks/UseReduxState'
import { getGuestRequestTime } from '../../../redux/requestSlice'

const RequestForm: React.FC = () => {

  const { getAccessToken, memberID } = useAuth0User();
  const { isMobile } = useResponsive();
  const { enqueueSnackbar } = useSnackbar();
  const { dateFormatted } = useDates();
  const { calcMaxArrival, calcMaxDeparture, calcMinArrival, calcMinDeparture, getOffSeasonStartDate, getRequestYearEndDate } = useSignupDates();

  const [arrivalDate, setArrivalDate] = React.useState<string>(EMPTY_STRING);
  const [departureDate, setDepartureDate] = React.useState<string>(EMPTY_STRING);
  const [maxArrival, setMaxArrival] = React.useState<string>(EMPTY_STRING);
  const [maxDeparture, setMaxDeparture] = React.useState<string>(EMPTY_STRING);
  const [minArrival, setMinArrival] = React.useState<string>(EMPTY_STRING);
  const [minDeparture, setMinDeparture] = React.useState<string>(EMPTY_STRING);

  const [members, setMembers] = React.useState<Array<Member>>(new Array<Member>());
  const [notes, setNotes] = React.useState<string>(EMPTY_STRING);
  const [priorityPeriods, setPriorityPeriods] = React.useState<Array<PriorityPeriod>>([]);
  const [requestStatus, setRequestStatus] = React.useState<HouseholdRequestStatus>(new HouseholdRequestStatus());
  const [transferablePasses, setTransferablePasses] = React.useState<Array<TransferablePassSignup>>([]);

  const [formDisabled, setFormDisabled] = React.useState<boolean>(true);
  const [helpDialogOpen, setHelpDialogOpen] = React.useState<boolean>(false);
  const [maxRequestsWarningDialogOpen, setMaxRequestsWarningDialogOpen] = React.useState<boolean>(false);
  const [memberWaivers, setMemberWaivers] = React.useState<Array<MemberWaiverStatus>>([]);
  const [postDeadlineAvailable, setPostDeadlineAvailable] = React.useState<boolean>(false);
  const [postDeadlineWarningDialogOpen, setPostDeadlineWarningDialogOpen] = React.useState<boolean>(false);
  const [priorityPeriodReset, setPriorityPeriodReset] = React.useState<boolean>(false);
  const [saveButtonEnabled, setSaveButtonEnabled] = React.useState<boolean>(false);
  const [transferablePassesAvailable, setTransferablePassesAvailable] = React.useState<boolean>(false);
  const [saveInProgress, setSaveInProgress] = React.useState<boolean>(false);
  const [waiverDialogOpen, setWaiverDialogOpen] = React.useState<boolean>(false);

  const [requestDates, setRequestDates] = React.useState<RequestDatesType>(RequestDatesType.None);
  const [offSeason, setOffSeason] = React.useState<boolean>(false);
  const [offSeasonStartDate] = React.useState<Date>(new Date(getOffSeasonStartDate()));
  const [membersSelected, setMembersSelected] = React.useState<number>(0);

  // Redux
  const guestRequestTime: number = useAppSelector(getGuestRequestTime);

  // #region Styles

  const theme = useTheme();
  const styles = {
    root: {
      margin: theme.spacing(0, 1, 0, 1),
    },
    actionsContainer: {
      padding: theme.spacing(2, 2, 0, 0),
    },
    componentTitle: {
      borderBottom: `1px solid ${theme.palette.divider}`,
      margin: theme.spacing(0, 0, 1, 0),
    },
    contentContainer: {
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(2, 2, 2, 2),
      maxWidth: 500,
    },
    contentContainerMobile: {
      padding: theme.spacing(2, 2, 2, 2),
    },
    guestMsgContainer: {
      margin: theme.spacing(1, 0, 1, 0),
    },
    membersContainer: {
      margin: theme.spacing(1, 0, 0, 0),
    },
    passesContainer: {
      borderTop: `1px solid ${theme.palette.divider}`,
      margin: theme.spacing(1, 0, 1, 0),
    },
    priortyPeriodsContainer: {
      margin: theme.spacing(1, 0, 1, 0),
    },
    notesTextBox: {
      width: '100%',
    }
  };

  // #endregion

  // #region Methods

  /*
  * Check if today is w/in a post deadline, if available
  */
  const checkDatesPostDeadline = (): boolean => {

    // Within postdeadline dates
    let check = false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Is there a post deadline and  is arrival before post deadline departure?
    if ((requestStatus.postDeadline) && (isBefore(new Date(arrivalDate), new Date(requestStatus.postDeadline.departureDate)))) {
      check = true;
    }

    return check;
  }

  /*
   * Is the departure date before the next priority period arrival date if exists?
   */
  const checkDepartureBeforeNextPriority = (): boolean => {

    let before = true;

    if ((priorityPeriods.length > 0) && (arrivalDate !== EMPTY_STRING) && (departureDate !== EMPTY_STRING)) {

      const d = new Date(departureDate);

      // Are arrival and departure before the next priority period?
      const arrivals = priorityPeriods.filter(pp => isAfter(new Date(pp.arrivalDate).getTime(), new Date(requestStatus.postDeadline.departureDate).getTime()));
      // If future priority periods exist, check them
      if (arrivals.length > 0) {
        // Add 1 day to next arrival for comparison
        const next = addDays(new Date(arrivals[0].arrivalDate), 1);
        // Is departue on or before the next priority arrival date+1 (adjusted)?
        before = isBefore(d.getTime(), next.getTime());
      }

      if (!before) {
        setPostDeadlineWarningDialogOpen(true);
      }
    }

    return before;
  }

  /*
  * Check if date selection is existing priority request
  */
  const checkExistingPriority = (): boolean => {

    if (
      (requestStatus.requests.length === 0)
      || (requestStatus.requests.length === requestStatus.maxRequests)
      || isAfter(new Date(arrivalDate), offSeasonStartDate)
      || (arrivalDate === EMPTY_STRING) || (departureDate === EMPTY_STRING)) {
      return false;
    }

    const a = new Date(arrivalDate);
    const d = new Date(departureDate);

    // Tests if an existing priority request date range overlaps w/ the selected arrival/departure
    const check = requestStatus.requests.some(r =>
      areIntervalsOverlapping(
        { start: a, end: d },
        { start: new Date(r.minArrivalDate), end: new Date(r.maxDepartureDate) }
      )
    );

    return check;
  }

  /*
  * Check if the date selection is an existing request
  */
  const checkExistingRequest = async () => {

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    // URL parameters
    var ids: number[] = [];
    members.forEach(member => {
      if (member.selected) {
        ids.push(member.memberID);
      }
    });

    const params = new URLSearchParams();
    params.append('arrivalDate', arrivalDate);
    params.append('departureDate', departureDate);
    params.append('ids', ids.join(','));

    const url = new URL(`${API_URL}/v1/requests/checkdatesoverlap`);
    url.search = params.toString();

    return await fetch(url.toString(), {
      method: 'GET',
      cache: 'no-cache',
      headers: headers,
      mode: 'cors'
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Unable to check existing request dates');
        }
        return response.json()
      })
      .then((sexc: SignupResultStatus) => {
        return (sexc.statusCode === SignupExceptionEnum.NewRequestDatesOverlap);
      })
      .catch(err => {
        throw new Error('Unable to check existing request dates:' + err);
      });

  }

  /*
  * Check if date selection is offseason
  */
  const checkOffSeason = (): boolean => {
    return (isAfter(new Date(arrivalDate), offSeasonStartDate));
  }

  /*
  * Check if date selection is non-weekend/holiday
  */
  const checkNonWeekendHoliday = (): boolean => {

    let check = false;

    if (isBefore(new Date(departureDate), offSeasonStartDate) && (priorityPeriods.length > 0) && (arrivalDate !== EMPTY_STRING && departureDate !== EMPTY_STRING)) {

      const a = new Date(arrivalDate);
      const d = new Date(departureDate);

      // Tests if a priority period date range does not overlaps w/ the selected arrival/departure
      check = !priorityPeriods.some(pp =>
        areIntervalsOverlapping(
          { start: a, end: d },
          { start: new Date(pp.arrivalDate), end: new Date(pp.departureDate) }
        )
      );
    }

    return check;
  }

  /*
  * Check if date selection overlaps a weekend/holiday
  */
  const checkWeekendHoliday = (): boolean => {

    let check = false;

    if ((priorityPeriods.length > 0) && (arrivalDate !== EMPTY_STRING && departureDate !== EMPTY_STRING)) {

      const a = new Date(arrivalDate);
      const d = new Date(departureDate);

      // Tests if a priority period date range overlaps w/ the selected arrival/departure
      check = priorityPeriods.some(pp =>
        areIntervalsOverlapping(
          { start: a, end: d },
          { start: new Date(pp.arrivalDate), end: new Date(pp.departureDate) }
        )
      );
    }

    return check;

  }

  /*
  * Clear data entry form
  */
  const clearForm = (): void => {

    // UI defaults
    setArrivalDate(EMPTY_STRING);
    setDepartureDate(EMPTY_STRING);
    setCalendarRestrictions();
    setNotes(EMPTY_STRING);
    setTransferablePasses([]);

    setPriorityPeriodReset(true);
    setSaveButtonEnabled(false);

    // Set each member as selected
    setMembers(members => {
      members.forEach(m => {
        m.selected = true;
      });
      return [...members];
    });

    // Date type icons
    if (offSeason) {
      setRequestDates(RequestDatesType.OffSeason);
    } else {
      setRequestDates(RequestDatesType.None);
    }

  }

  /*
  * Get household request status, i.e., max priority requests
  */
  const fetchHouseholdRequestStatus = async () => {

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    const id = memberID();
    const url = new URL(`${API_URL}/v1/requests/householdrequeststatus/${id}`);

    return await fetch(url.toString(), {
      method: 'GET'
      , cache: 'no-cache'
      , headers: headers
      , mode: 'cors'
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Unable to get household request status');
        }
        return response.json()
      })
      .then((rs: HouseholdRequestStatus) => {
        setRequestStatus(rs);
      })
      .catch(err => {
        throw new Error('Unable to get household request status');
      });
  }

  /*
   * Fetch household member data for signup
   */
  const fetchMembers = async () => {

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    const id = memberID();
    const url = new URL(`${API_URL}/v1/households/${id}/members/request`);

    await fetch(url.toString(), {
      method: 'GET',
      cache: 'no-cache',
      headers: headers,
      mode: 'cors'
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Household members not retrieved for signup form');
        }
        return response.json()
      })
      .then(household => {
        const mbrs = household.members.map(
          (m: Member) => {
            // Default selected=true
            m.selected = true;
            return m;
          });

        setMembers(mbrs);
        // All members selected by default
        setMembersSelected(mbrs.length);
      })
      .catch(err => {
        throw new Error('Household members not retrieved for signup form');
      });
  }

  /*
  * Get household waivers
  */
  const fetchMemberWaivers = async () => {

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    const id = memberID();
    const url = new URL(`${API_URL}/v1/members/memberwaivers/${id}`);

    return await fetch(url.toString(), {
      method: 'GET',
      cache: 'no-cache',
      headers: headers,
      mode: 'cors'
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Unable to get member waiver status');
        }
        return response.json()
      })
      .then((ws: Array<MemberWaiverStatus>) => {
        setMemberWaivers(ws);
      })
      .catch(err => {
        throw new Error('Unable to get member waiver status');
      });
  }

  /*
  * Get list of priority periods
  */
  const fetchPriorityPeriods = async () => {

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    const params = new URLSearchParams();
    if (requestStatus.postDeadline) {
      params.append('startDate', requestStatus.postDeadline.departureDate);
    } else {
      // Today forward
      const today: Date = new Date();
      params.append('startDate', format(today, 'MM/d/y'));
    }
    params.append('endDate', getRequestYearEndDate());

    const url = new URL(`${API_URL}/v1/requests/priorityperiods`);
    url.search = params.toString();

    await fetch(url.toString(), {
      method: 'GET',
      cache: 'no-cache',
      headers: headers,
      mode: 'cors'
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Priority period dates were not retrieved.');
        }
        return response.json()
      })
      .then((periods: Array<PriorityPeriod>) => {
        setPriorityPeriods(periods);
      })
      .catch(err => {
        throw new Error('Priority period dates were not retrieved.');
      });
  }

  /*
  * Check today is after a priority deadline
  */
  const isPostDeadlineAvailable = () => {

    let check = false;

    if (requestStatus.postDeadline) {
      // Tests if today if after request deadline
      const today = new Date();
      check = (isAfter(today, new Date(requestStatus.postDeadline.signupDeadline)) && isBefore(today, new Date(requestStatus.postDeadline.departureDate)))
    }

    setPostDeadlineAvailable(check);
  }

  /*
   * Package signup data from UI before save
   */
  const packageData = (): SignupNew => {

    // Members included in signup
    const memberSignups = new Array<MemberSignupInputForm>();
    members.forEach(member => {
      if (member.selected) {
        memberSignups.push(new MemberSignupInputForm(
          0, // memberSignupID
          0, // signupID
          member.memberID,
          new Date(arrivalDate),
          new Date(departureDate),
          false // cancel
        ));
      }
    });

    // Signup
    const id = memberID();
    const signupForm = new SignupNew(
      id, // MemberID = userID
      memberSignups,
      notes,
      transferablePasses,
      id // userID
    );

    return signupForm;
  }

  /*
  * Save lodge use request
  */
  const save = async () => {

    setSaveButtonEnabled(false);

    // Show dialog w/ spinner
    setSaveInProgress(true);

    // Package signup data for save
    const signup = packageData();

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    await fetch(API_URL + '/v1/signups/insert', {
      method: 'POST',
      body: JSON.stringify(signup),
      headers: headers,
      mode: 'cors'
    })
      .then((response) => {
        setSaveInProgress(false);
        if (response.ok) {
          // Get request summary to see if priority request count has changed
          fetchHouseholdRequestStatus();
          clearForm();
          enqueueSnackbar(`Lodge use request for ${arrivalDate} saved and acknowledgement email sent`, { variant: 'success' });
        } else {
          enqueueSnackbar('Lodge use request did not save. Please report using the Feedback form.', { variant: 'error' });
        }
      })
      .catch(error => {
        setSaveInProgress(false);
        enqueueSnackbar('Lodge use request did not save. Please report using the Feedback form.', { variant: 'error' });
      })
  }

  /*
   * Set calendar restrictions based upon mode etc
   */
  const setCalendarRestrictions = () => {

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = addDays(today, 1);
    tomorrow.setHours(0, 0, 0, 0);

    setMinArrival(dateFormatted(today));
    setMinDeparture(dateFormatted(tomorrow));
    setMaxArrival(getRequestYearEndDate());
    setMaxDeparture(getRequestYearEndDate());
  }

  /*
  * Validate request for required data and set request type
  */
  const validateRequest = async () => {

    // Arrival & departure required to Save
    if (arrivalDate === EMPTY_STRING || departureDate === EMPTY_STRING) {
      setSaveButtonEnabled(false);
      return;
    }

    // Dates an existing priority request?
    const existingPriority = checkExistingPriority();
    // Default: set below if necessary
    let existingRequest = false;

    // Warnings
    if (existingPriority) {
      enqueueSnackbar(`Dates overlap with an existing priority request.`, { variant: 'warning' });
      setSaveButtonEnabled(false);
      //return;
    } else {
      // Dates overlap w/ an existing request
      existingRequest = await checkExistingRequest();
      if (existingRequest) {
        enqueueSnackbar(`Dates overlap with an existing request.`, { variant: 'warning' });
        setSaveButtonEnabled(false);
        // return;
      }
    }

    // Dates offseason
    const offseason = checkOffSeason();

    // Request Type
    // Sequence of checks is important...
    if (offseason) {
      setRequestDates(RequestDatesType.OffSeason);
      setSaveButtonEnabled(existingRequest || existingPriority ? false : true);
    } else {
      // Dates in post deadline?
      const postDeadline = checkDatesPostDeadline();
      if (postDeadline) {
        if (!checkDepartureBeforeNextPriority()) {
          setPostDeadlineWarningDialogOpen(true);
          setSaveButtonEnabled(false);
        } else {
          setRequestDates(RequestDatesType.PostDeadline);
          setSaveButtonEnabled(existingRequest || existingPriority ? false : true);
        }
      } else {
        // Dates weekend/holiday?
        const weekend = checkWeekendHoliday();
        if (weekend) {
          if (requestStatus.requests.length >= requestStatus.maxRequests) {
            setMaxRequestsWarningDialogOpen(true);
            setSaveButtonEnabled(false);
          } else {
            setRequestDates(RequestDatesType.WeekendHoliday);
            setSaveButtonEnabled(existingRequest || existingPriority ? false : true);
          }
        } else {
          // Dates non-weekend/holiday?
          const nonweekend = checkNonWeekendHoliday();
          if (nonweekend) {
            setRequestDates(RequestDatesType.NonWeekendHoliday);
            setSaveButtonEnabled(existingRequest || existingPriority ? false : true);
          }
        }
      }
    }

  }

  // #endregion

  // #region Event handlers

  /*
  * Event handler for changes to arrival date
  */
  const handleArrivalDateChange = (date: Date | null) => {

    // Test valid date format
    if (!isDateValid(date)) {
      return;
    }

    const formattedDate = date ? dateFormatted(date) : EMPTY_STRING;

    // Clear departure if present and >= arrival
    if (formattedDate && date) {
      if (departureDate) {
        const departure: Date = new Date(departureDate);
        departure.setHours(0, 0, 0, 0);
        const arrival: Date = new Date(formattedDate);
        arrival.setHours(0, 0, 0, 0);
        if (departure <= arrival) {
          setDepartureDate(EMPTY_STRING);
          enqueueSnackbar(`Clearing departure date because new arrival is later. Re-select departure date`, { variant: 'info' });
        }
      }
    }

    setArrivalDate(formattedDate);
  };

  /*
   * Handle clear form
   */
  const handleClear = () => {
    clearForm();
  }

  /*
   * Handler for changes to departure date
   */
  const handleDepartureDateChange = (date: Date | null) => {
    // Test valid date format
    if (!isDateValid(date)) {
      return;
    }

    setDepartureDate(date ? dateFormatted(date) : EMPTY_STRING);

  };

  /*
  * Handler for help dialog window
  */
  const handleHelpDialogClose = () => {
    setHelpDialogOpen(false);
  }

  /*
   * Hvent handler for change in check (selection) of a member
   */
  const handleMemberCheckChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Copy array to process
    const m = [...members];
    const i = m.findIndex(m => { return m.memberID === Number(event.target.value) });
    m[i].selected = event.target.checked;

    // Check members selected
    let s = 0;
    m.forEach(m => {
      if (m.selected) {
        s++;
      }
    });
    setMembersSelected(s);

    // Update state
    setMembers([...m]);
  };

  /*
   * Handler for change in note text
   */
  const handleNotesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(event.target.value);
  }

  /*
   * Handle changes to transferable pass requests
   */
  const handlePassesChange = (passes: Array<TransferablePassSignup>) => {
    setTransferablePasses(passes);
  }

  /*
  * Handler for non-weekend/holiday warning dialog window
  */
  const handleMaxRequestsWarningDialogClose = () => {
    setMaxRequestsWarningDialogOpen(false);
  }

  /*
  * Handler for post deadline warning dialog window
  */
  const handlePostDeadlineWarningDialogClose = () => {
    setPostDeadlineWarningDialogOpen(false);
  }

  /*
   * Handler for changed priority period
   */
  const handlePriorityPeriodChange = (period: PriorityPeriod) => {

    // Set selected dates
    setArrivalDate(period.arrivalDate);
    setDepartureDate(period.departureDate);

    // Reset flag once a period has been selected
    setPriorityPeriodReset(false);
  }

  /*
  * Handler for waiver dialog window
  */
  const handleWaiverDialogClose = () => {
    setWaiverDialogOpen(false);
  }

  React.useEffect(() => {
    if (offSeasonStartDate !== undefined) {
      fetchMemberWaivers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offSeasonStartDate]);

  React.useEffect(() => {
    // Request count >= 0 indicates data has been retrived from API
    if (requestStatus.requestCount >= 0) {
      // Are priority requests available and are there priority periods upcoming?
      if (requestStatus.futurePriorityPeriods) {
        fetchPriorityPeriods();
      }
      fetchMembers();
      // Check if existing requests overlap post deadline if avalable
      isPostDeadlineAvailable();
      clearForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestStatus.requestCount]);

  React.useEffect(() => {
    if (members.length > 0) {
      setFormDisabled(false);
    }
  }, [members.length]);

  React.useEffect(() => {

    if (arrivalDate) {
      setMaxDeparture(calcMaxDeparture(arrivalDate));
      setMinDeparture(calcMinDeparture(arrivalDate));

      if (departureDate && (isBefore(new Date(arrivalDate), offSeasonStartDate))) {
        setTransferablePassesAvailable(true);
      }
    } else {
      setTransferablePassesAvailable(false);
    }

    if (departureDate) {
      setMaxArrival(calcMaxArrival(departureDate));
      setMinArrival(calcMinArrival(departureDate));
      if (arrivalDate && (isBefore(new Date(arrivalDate), offSeasonStartDate))) {
        setTransferablePassesAvailable(true);
      }
    }

    validateRequest();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrivalDate, departureDate]);

  React.useEffect(() => {
    // Check if 1+ members selected
    if (membersSelected === 0) {
      setSaveButtonEnabled(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membersSelected]);

  React.useEffect(() => {

    if (offSeasonStartDate !== undefined) {
      // Set offseason flag in local state
      // Today >= offseason start date
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      setOffSeason(today >= offSeasonStartDate);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offSeasonStartDate]);

  React.useEffect(() => {
    if ((memberWaivers.length > 0) && memberWaivers.some(m => m.waiverStatusID === WaiverStatus.Pending)) {
      setWaiverDialogOpen(true);
    } else {
      fetchHouseholdRequestStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(memberWaivers)]);

  // #endregion

  return (
    <>
      <Box sx={styles.root}>
        <Box sx={isMobile ? styles.contentContainerMobile : styles.contentContainer}>
          <Grid container alignItems="center" sx={styles.componentTitle}>
            <Typography variant="h6">Lodge Use Request</Typography>
            <Tooltip title="Lodge use request help">
              <IconButton onClick={() => setHelpDialogOpen(true)} size="large">
                <HelpIcon />
              </IconButton>
            </Tooltip>
          </Grid>
          {!offSeason &&
            <>
              <WeekendHoliday
                postDeadlineAvailable={postDeadlineAvailable}
                requestStatus={requestStatus}
              />
              <Divider />
              <Grid container justifyContent="center" sx={styles.priortyPeriodsContainer}>
                <PriorityPeriodList
                  disabled={formDisabled}
                  handlePriorityPeriodChange={handlePriorityPeriodChange}
                  priorityPeriods={priorityPeriods}
                  reset={priorityPeriodReset}
                />
              </Grid>
            </>
          }
          <Grid container spacing={1} justifyContent="center">
            <Grid size={5}>
              <DatePicker
                referenceDate={minArrival ? new Date(minArrival) : undefined}
                disabled={formDisabled}
                disablePast
                label="Arrival"
                minDate={minArrival ? new Date(minArrival) : undefined}
                maxDate={maxArrival ? new Date(maxArrival) : undefined}
                onChange={handleArrivalDateChange}
                value={arrivalDate === EMPTY_STRING ? null : new Date(arrivalDate)}
                views={['year', 'month', 'day']}
              />
            </Grid>
            <Grid size={5}>
              <DatePicker
                referenceDate={arrivalDate ? new Date(arrivalDate) : undefined}
                disabled={formDisabled}
                disablePast
                format="MM/dd/yyyy"
                label="Departure"
                minDate={minDeparture ? new Date(minDeparture) : undefined}
                maxDate={maxDeparture ? new Date(maxDeparture) : undefined}
                onChange={handleDepartureDateChange}
                value={departureDate === EMPTY_STRING ? null : new Date(departureDate)}
                views={['year', 'month', 'day']}
              />
            </Grid>
          </Grid>
          <Grid container sx={styles.membersContainer}>
            {members.map(m => (
              <Grid container spacing={0} key={m.memberID}>
                <FormControl disabled={formDisabled}>
                  <FormControlLabel
                    control={<Checkbox key={m.memberID} onChange={handleMemberCheckChange} value={m.memberID} checked={m.selected} />}
                    label={`${m.firstName}  ${m.lastName} (${m.memberStatus.status})`}
                  />
                </FormControl>
              </Grid>
            ))}
          </Grid>
          {(transferablePassesAvailable) &&
            <Grid container sx={styles.passesContainer}>
              <Typography variant="subtitle2" mt={2} gutterBottom>Transferable Pass Requests</Typography>
              <TransferablePasses
                handlePassesChange={handlePassesChange}
                minArrivalDate={arrivalDate}
                maxDepartureDate={departureDate}
                offSeasonStartDate={offSeasonStartDate}
                passes={transferablePasses}
              />
            </Grid>
          }
          <Grid container>
            <Typography variant="subtitle2" gutterBottom>Notes</Typography>
            <TextareaAutosize
              style={styles.notesTextBox}
              disabled={formDisabled}
              maxRows={10}
              minRows={5}
              onChange={handleNotesChange}
              placeholder="Enter additional info such as bedroom sharing, no dinner, carpool, etc."
              value={notes}
            />
          </Grid>
          <Grid container sx={styles.guestMsgContainer}>
            <Typography variant="caption"><b>IMPORTANT:</b> Add guests in My Requests w/in {guestRequestTime} min to have the same priority.</Typography>
          </Grid>
          <Divider />
          <Grid container spacing={2} justifyContent="right" sx={styles.actionsContainer}>
            <Grid>
              <Stack direction="row" spacing={2}>
                {requestDates === RequestDatesType.OffSeason &&
                  <Chip icon={<HikingIcon />} label="Off Season" />
                }
                {requestDates === RequestDatesType.PostDeadline &&
                  <Chip icon={<WatchLaterIcon />} label="Post Deadline" />
                }
                {requestDates === RequestDatesType.NonWeekendHoliday &&
                  <Chip icon={<CabinIcon />} label="Non-Weekend/Holiday" />
                }
                {requestDates === RequestDatesType.WeekendHoliday &&
                  <Chip icon={<DownhillSkiingIcon />} label="Weekend/Holiday" />
                }
              </Stack>
            </Grid>
            <Grid>
              <Tooltip title="Clear reset form" arrow>
                <span>
                  <Button variant="outlined" size={isMobile ? 'small' : 'medium'} color="primary" onClick={handleClear}>Clear</Button>
                </span>
              </Tooltip>
            </Grid>
            <Grid>
              <Tooltip title="Save lodge use request" arrow>
                <span>
                  <Button variant="contained" startIcon=<SaveIcon /> disabled={!saveButtonEnabled || formDisabled ? true : false} size={isMobile ? 'small' : 'medium'} color="primary" onClick={save}>Save</Button>
                </span>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <MaxRequestsWarningDialog
        handleClose={handleMaxRequestsWarningDialogClose}
        show={maxRequestsWarningDialogOpen}
      />
      <PostDeadlineWarningDialog
        handleClose={handlePostDeadlineWarningDialogClose}
        requestStatus={requestStatus}
        show={postDeadlineWarningDialogOpen}
      />
      <RequestFormHelpDialog
        handleClose={handleHelpDialogClose}
        maxRequests={requestStatus.maxRequests}
        show={helpDialogOpen}
      />
      <Dialog
        open={saveInProgress}
        fullWidth={true}
        maxWidth='xs'
      >
        <DialogTitle>
          <Typography variant="inherit">Saving {arrivalDate} Lodge Use Request</Typography>
        </DialogTitle>
        <DialogContent>
          <Grid
            container
            spacing={0}
            justifyContent="center"
          >
            <CircularProgress />
          </Grid>
        </DialogContent>
      </Dialog>
      <WaiverDialog
        handleClose={handleWaiverDialogClose}
        memberWaivers={memberWaivers}
        show={waiverDialogOpen}
        handleWaiverSigned={fetchMemberWaivers}
      />
    </>
  );

}

export default RequestForm;